import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import type { PaginationParams } from "@features/vessel-management/components/RegisteredVessels/RegisteredVessels";
import {
  type BunkerBargeDetailsPayload,
  type Vessel,
  type VesselRequestPayload,
  type VesselsResponse,
  type VesselStatusPayload,
} from "@services/vessel/types";
import { api } from "./api";

export interface VesselsParams {
  pageSize?: number;
  pageNumber?: number;
  imoNumber?: string;
  vesselName?: string;
}

const BFF_VESSEL = "bff-vessel";

const transformFiltersToPayload = (paginationParams: PaginationParams, filters: FilterItem[]) => {
  const payload: Record<string, any> = {
    pageSize: paginationParams.pageSize,
    pageNumber: paginationParams.page,
  };

  filters.forEach(({ field, value }) => {
    payload[field] = [...(payload[field] ?? []), value];
  });

  return payload;
};

export const getSearchVessels = async (
  service: string,
  paginationParams: PaginationParams,
  filters: FilterItem[],
) => {
  return api.post<VesselsResponse>(
    service,
    "vessels:search",
    transformFiltersToPayload(paginationParams, filters),
  );
};

export const getScanVessels = async (
  service: string,
  value: string,
  pageNumber: number,
  pageSize: number,
) => {
  return api.get<VesselsResponse>(service, "vessels:scan", {
    params: {
      imoOrName: value,
      pageNumber,
      pageSize,
    },
  });
};

export const getVessels = async (service: string, params: VesselsParams, signal?: AbortSignal) => {
  return api.get<VesselsResponse>(service, "vessels", { params, signal: signal });
};

export const getVesselById = async (service: string, id?: string) => {
  return api.get<Vessel>(service, `vessels/${id}`);
};

export const getVesselByIdNoCompanyIdValidation = async (service: string, id?: string) => {
  return api.get<Vessel>(service, `vessels/${id}:without-company-id-filtering`);
};

export const postVessel = async ({
  vesselPayload,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.post<VesselRequestPayload>(
    BFF_VESSEL,
    "vessels",
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const putVessel = async ({
  id,
  vesselPayload,
  transactionId,
}: {
  id: string;
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.put(
    BFF_VESSEL,
    `vessels/${id}`,
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const postDraftVessel = async ({
  vesselPayload,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  transactionId?: string;
}) => {
  return api.post<VesselRequestPayload>(
    BFF_VESSEL,
    "vessels/drafts",
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const patchDraftVessel = async ({
  vesselPayload,
  id,
  transactionId,
}: {
  vesselPayload: VesselRequestPayload;
  id: string;
  transactionId?: string;
}) => {
  return api.patch(
    BFF_VESSEL,
    `vessels/${id}/drafts`,
    vesselPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const discardDraftVessel = async ({
  id,
  transactionId,
}: {
  id: string;
  transactionId?: string;
}) => {
  return api.delete(
    BFF_VESSEL,
    `vessels/${id}/drafts`,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateVesselStatus = async ({
  service,
  id,
  vesselStatusPayload,
  transactionId,
}: {
  service: string;
  id: string;
  vesselStatusPayload: VesselStatusPayload;
  transactionId?: string;
}) => {
  return api.patch(
    service,
    `vessels/${id}/status`,
    vesselStatusPayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updateBunkerBarge = async ({
  service,
  id,
  bunkerBargePayload,
  transactionId,
}: {
  service: string;
  id: string;
  bunkerBargePayload: BunkerBargeDetailsPayload;
  transactionId?: string;
}) => {
  return api.patch(
    service,
    `vessels/${id}/update-bb`,
    bunkerBargePayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};
